// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { combineLatest, from, Observable, of, ReplaySubject } from 'rxjs';
import { filter, first, map, scan, switchMap, tap } from 'rxjs/operators';

import {
  fromCarl,
  selectCommonAbTestingUserConsent,
  selectCommonIsConsentSystemInitializationDone,
  selectIsInIntegratedMode
} from 'src/app/store/common/common.selectors';
import { firstValueFromSync, fromRouterAsSnapshot } from 'src/app/store/router/next-router.selectors';
import { secureRandom } from 'src/app/utils/functions/secure-random';
import { AB_TESTING_TYPE_PLACEHOLDER, OWCC_TICKET_PREFIX } from 'src/app/utils/global-constants';

import { OWCC_LOCAL_STORAGE_KEY } from '../../config/web-storage.config';
import { SettingsService } from '../settings/settings.service';
import { LocalStorageService } from '../storage/local-storage/local-storage.service';
import { TrackingAbTest } from '../tracking/models/tracking-object.interface';

export enum AbTestingFeatureType {
  Required = 'required',
  Optional = 'optional'
}

enum AbTestingLocalStorageKeyPart {
  Required = 'required',
  Optional = 'ab-testing'
}

const FEATURE_TYPE_TO_LOCAL_STORAGE_KEY_PART_MAPPING = {
  [AbTestingFeatureType.Optional]: AbTestingLocalStorageKeyPart.Optional,
  [AbTestingFeatureType.Required]: AbTestingLocalStorageKeyPart.Required
};

export interface AbTestingFeature {
  /**
   * e.g 1234 (without 'OWCC-' prefix)
   */
  storyNumber: string;

  /**
   * e.g. 2 (results in user groups A and B), 3 (results in user groups A, B, C). Supported 2-5;
   */
  variants: number;

  isBackend?: boolean;
}

export interface TypedAbTestingFeature extends AbTestingFeature {
  type: AbTestingFeatureType;
}

export interface AbTestingConfig {
  active: boolean;
  features: AbTestingFeature[];
}

interface ExtractUserGroupFromQueryParamResult {
  isValidStoryNumberAndValidUserGroup: boolean;
  userGroupFromQueryParam: AbTestingUserGroup;
}

interface ExtractUserGroupFromFromUrlResult {
  hasUserGroupFromQueryParam: boolean;
  userGroupFromQueryParam: AbTestingUserGroup;
}

export type AbTestingUserGroup = 'A' | 'B' | 'C' | 'D' | 'E';
const USER_GROUP_MAP: AbTestingUserGroup[] = ['A', 'B', 'C', 'D', 'E'];
const DEFAULT_USER_GROUP = 'A';

/**
 * AbTestingService is used for A/B-Testing.
 *
 * For more info read `ab-testing.md`.
 */
@Injectable({
  providedIn: 'root'
})
export class AbTestingService {
  private enabledOptionalFeatures: TypedAbTestingFeature[] = [];

  /**
   * Basically emits twice.
   * - First time when initilization is done for technical required features - no CMP or user consent needed.
   * - Second time when CMP is loaded and user decision is done (no matter if user gave consent or declined).
   *
   * NOTE: all this happens _after_ config fully got loaded.
   */
  private readonly abTestingInitializationDone$: ReplaySubject<void> = new ReplaySubject<void>(1);

  private readonly abTestingInitializationBasicallyDone: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private readonly abTestingInitializationCompletelyDone: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  public constructor(
    private readonly settingsService: SettingsService,
    private readonly localStorageService: LocalStorageService,
    private readonly store: Store
  ) {
    this.abTestingInitializationDone$.pipe(first()).subscribe(() => {
      this.abTestingInitializationBasicallyDone.next(true);
      this.abTestingInitializationCompletelyDone.next(true);
    });

    this.init();
  }

  /**
   * Returns (observable of) true if the ab initialization is _basically_ done (false otherwise).
   * This means that least the _technically required features_ are set (local storage).
   * Optional features not considered (since this requires consent from the user which he/she might not give right away).
   */
  public get abTestingInitializationBasicallyDone$(): Observable<boolean> {
    return this.abTestingInitializationBasicallyDone.asObservable();
  }

  /**
   * Returns (observable of) true if the ab initialization is _completely_ done (false otherwise).
   * This means that the decision about _optional features_ is done (and updated in local storage).
   */
  public get abTestingInitializationCompletelyDone$(): Observable<boolean> {
    return this.abTestingInitializationCompletelyDone.asObservable();
  }

  public get hasAbTestingUserConsent$(): Observable<boolean> {
    return this.store.select(selectCommonAbTestingUserConsent);
  }

  private get isConsentSystemInitializationDone$(): Observable<boolean> {
    return this.store.select(selectCommonIsConsentSystemInitializationDone);
  }

  private get isActiveForEnvironment$(): Observable<boolean> {
    const isActiveInStandaloneMode = this.settingsService.data.abTesting?.activeInStandaloneMode;
    // Intentionally do not initialize AB testing for standalone mode (except configured in setting for e.g. local testing)
    // Standalone mode does not support tracking, thus generally no AB testing needed here.

    return this.store
      .select(selectIsInIntegratedMode)
      .pipe(switchMap(isInIntegratedMode => of(isInIntegratedMode || isActiveInStandaloneMode)));
  }

  /**
   * @param storyNumber - story number as string without prefix, e.g. '1234'
   * @returns an observable of a user group (e.g. 'A') by ticket number.
   * - Always returns 'A' (default behaviour) in case of standalone mode (and not specified differently in settings).
   * - Also returns 'A' if not configured as active in market config (=> no localstorage entry).
   * - If there is a (valid) URL query parameter, it returns the according value (no matter what's in the storage!). Format: https://whatever/?ab=1234-A&ab=2345-B
   *
   * NOTE: you might NOT want to use the `first()` operator when requesting a user group (depending on the use case).
   * Generally it is possible that a user gets a OWCC deep link to e.g. the summary page but was never on (e.g.) www.mercedes-benz.de before.
   * If so, he never gave or declined consent and the consent layer will show up.
   * Of course getting a user group is not blocking (but async) and if the decision about consent is still not done yet
   * the method already returns A (which is the default user group).
   * But: the user might then give consent to A/B testing, so the newly calculated user group might still be A, but also B (or even C, D, E, depending on the config).
   * If the UI should be adjusted depending on the updated user group (which not always make sense!) there should be not first() operator.
   * Another use case is if the user changes consent for AB-testing during the OWCC session (a button for changing consent might be available depending on the CMP settings).
   *
   * NOTE: ab testing might NOT be initialized completely (e.g. because config is not completely loaded).
   * You can additionally check this by calling abTestingInitializationBasicallyDone().
   */
  public getUserGroupByStoryNumber(storyNumber: string): Observable<AbTestingUserGroup> {
    const checkUrlForRelevantQueryParam = (relevantStoryNumber: string): string | undefined => {
      const abTestingQueryParam = firstValueFromSync<string>(
        this.store.pipe(fromRouterAsSnapshot.selectAbTestingQueryParam)
      );
      return abTestingQueryParam && abTestingQueryParam.split('-')[0] === relevantStoryNumber
        ? abTestingQueryParam
        : undefined;
    };

    const extractUserGroupFromQueryParam = (
      relevantAbTestingQueryParam: string,
      relevantStoryNumber: string
    ): ExtractUserGroupFromQueryParamResult => {
      const firstValidParamAsSplitStrings: string[] = relevantAbTestingQueryParam.split('-');
      const userGroupFromQueryParam: AbTestingUserGroup =
        firstValidParamAsSplitStrings[1]?.toUpperCase() as AbTestingUserGroup;

      const isValidStoryNumberAndValidUserGroup = this.getAllEnabledFeatures().some(
        feature =>
          feature.storyNumber === relevantStoryNumber &&
          USER_GROUP_MAP.slice(0, feature.variants).includes(userGroupFromQueryParam)
      );
      return { isValidStoryNumberAndValidUserGroup, userGroupFromQueryParam };
    };

    const extractUserGroupFromFromUrlIfAvailable = (relevantStoryNumber: string): ExtractUserGroupFromFromUrlResult => {
      const relevantAbTestingQueryParam = checkUrlForRelevantQueryParam(relevantStoryNumber);
      const hasRelevantAbTestingQueryParameter = relevantAbTestingQueryParam !== undefined;
      if (hasRelevantAbTestingQueryParameter) {
        const { isValidStoryNumberAndValidUserGroup, userGroupFromQueryParam } = extractUserGroupFromQueryParam(
          relevantAbTestingQueryParam,
          relevantStoryNumber
        );
        if (isValidStoryNumberAndValidUserGroup) {
          return {
            hasUserGroupFromQueryParam: true,
            userGroupFromQueryParam: userGroupFromQueryParam || DEFAULT_USER_GROUP
          };
        }
      }

      return { hasUserGroupFromQueryParam: false, userGroupFromQueryParam: null };
    };

    const getUserGroupFromLocalStorageOrDefault = (
      type: AbTestingFeatureType,
      relevantStoryNumber: string
    ): AbTestingUserGroup =>
      this.getLocalStorageValue(type, relevantStoryNumber) || (DEFAULT_USER_GROUP as AbTestingUserGroup);

    return combineLatest([
      this.isConsentSystemInitializationDone$,
      this.hasAbTestingUserConsent$,
      this.isActiveForEnvironment$,
      this.abTestingInitializationDone$
    ]).pipe(
      switchMap(([isConsentSystemInitializationDone, hasAbTestingUserConsent, isActiveForEnvironment]) =>
        of(extractUserGroupFromFromUrlIfAvailable(storyNumber)).pipe(
          map(({ hasUserGroupFromQueryParam, userGroupFromQueryParam }) => [
            isConsentSystemInitializationDone,
            hasAbTestingUserConsent,
            { hasUserGroupFromQueryParam, userGroupFromQueryParam },
            isActiveForEnvironment
          ])
        )
      ),
      map(
        ([
          isConsentSystemInitializationDone,
          hasAbTestingUserConsent,
          { hasUserGroupFromQueryParam, userGroupFromQueryParam },
          isActiveForEnvironment
        ]: [boolean, boolean, ExtractUserGroupFromFromUrlResult, boolean]) => {
          if (!isActiveForEnvironment) {
            return DEFAULT_USER_GROUP as AbTestingUserGroup;
          }

          if (hasUserGroupFromQueryParam) {
            return userGroupFromQueryParam || DEFAULT_USER_GROUP;
          }

          // here the initialization is done in the a way that the _CMP is ready_ and _user decided to give consent or not_.
          if (!isConsentSystemInitializationDone || !hasAbTestingUserConsent) {
            return DEFAULT_USER_GROUP as AbTestingUserGroup;
          }

          const isEnabledFeature = this.getAllEnabledFeatures().some(
            (feature: AbTestingFeature): boolean => feature.storyNumber === storyNumber
          );

          return isEnabledFeature
            ? getUserGroupFromLocalStorageOrDefault(AbTestingFeatureType.Optional, storyNumber)
            : (DEFAULT_USER_GROUP as AbTestingUserGroup);
        }
      )
    );
  }

  /**
   * Returns true if any of the configured backend features have a user group different than the default user group (which is A).
   * This can be used to check whether a request has to be done again (if a user enters the OWCC and later on accepts consent for AB testing).
   * Ideally this would have been checked for each individual feature to prevent extra backend requests. But as we assume
   * that only _one_ backend feature is available at a time (in optional section, ignoring technically required section) this approach is sufficient (for now).
   */
  public doOptionalBackendFeaturesHaveNonDefaultUserGroup(): Observable<boolean> {
    return from(this.enabledOptionalFeatures).pipe(
      filter(feature => feature.isBackend),
      switchMap((feature: TypedAbTestingFeature) => this.getUserGroupByStoryNumber(feature.storyNumber)),
      scan((previous: boolean, current: AbTestingUserGroup): boolean => !!(previous || current !== 'A'), false)
    );
  }

  /**
   * Note: Calling this method before getting the user group by story number is optional.
   * Its always possible to request a user group by story number.
   * If AB testing is not 'active' for any reason (no consent, not activated in config, wrong environment) it always returns a valid fallback (user group 'A')
   */
  public isFeatureEnabled(storyNumber: string): Observable<boolean> {
    return this.abTestingInitializationDone$.pipe(
      concatLatestFrom(() => this.isActiveForEnvironment$),
      switchMap(([, isActiveForEnvironment]): Observable<boolean> => {
        if (!isActiveForEnvironment) {
          return of(false);
        }

        return of(
          this.getAllEnabledFeatures().some((feature: AbTestingFeature): boolean => feature.storyNumber === storyNumber)
        );
      })
    );
  }

  public getAllEnabledBackendFeatures(): TypedAbTestingFeature[] {
    return this.getAllEnabledFeatures().filter(feature => feature.isBackend);
  }

  public getAllEnabledFeatures(): TypedAbTestingFeature[] {
    return [...this.enabledOptionalFeatures];
  }

  public getAbTestingTracking$(): Observable<TrackingAbTest> {
    const observableArray: Observable<string>[] = [];
    const enabledFeatures = this.getAllEnabledFeatures();
    for (const abTestingFeature of enabledFeatures) {
      observableArray.push(
        this.getUserGroupByStoryNumber(abTestingFeature.storyNumber).pipe(
          map(userGroup => `${OWCC_TICKET_PREFIX + abTestingFeature.storyNumber}:${userGroup}`)
        )
      );
    }

    if (observableArray.length > 0) {
      return combineLatest(observableArray).pipe(map(values => ({ abTest: values.join(';') })));
    }

    return of({ abTest: undefined });
  }

  private treatOptionalFeatures(config: AbTestingConfig): Observable<boolean> {
    return combineLatest([
      this.isConsentSystemInitializationDone$,
      this.hasAbTestingUserConsent$,
      this.isActiveForEnvironment$
    ]).pipe(
      filter(([isConsentSystemInitializationDone]) => isConsentSystemInitializationDone),
      tap(([, hasAbTestingUserConsent, isActiveForEnvironment]) => {
        const isAbTestingActive = isActiveForEnvironment && hasAbTestingUserConsent;

        if (isAbTestingActive) {
          const isAbTestingInMarketEnabled = config.active;
          if (isAbTestingInMarketEnabled) {
            this.enabledOptionalFeatures = this.toTypedAbTestingFeatures(
              config.features,
              AbTestingFeatureType.Optional
            );
            this.setUserGroupsInLocalStorage(this.enabledOptionalFeatures);
          }
        }
        this.abTestingInitializationDone$.next();
      }),
      map(([, hasAbTestingUserConsent]) => hasAbTestingUserConsent)
    );
  }

  private setUserGroupsInLocalStorage(features: TypedAbTestingFeature[]): void {
    for (const feature of features) {
      let storageValue = this.getLocalStorageValue(feature.type, feature.storyNumber);
      if (!storageValue) {
        const storageKey = this.getLocalStorageKey(feature.type, feature.storyNumber);
        const randomNumber = Math.floor(secureRandom() * feature.variants);
        storageValue = USER_GROUP_MAP[randomNumber] || USER_GROUP_MAP[0];
        this.localStorageService.set(storageKey, storageValue);
      }
    }
  }

  private forEachAbTestingLocalStorageEntry(callback: (storageKey: string) => void): void {
    this.localStorageService.forEachEntry((storageKey: string) => {
      const isAbTestingLocalStorageKey =
        storageKey.startsWith(this.getLocalStorageKeyPrefix(AbTestingFeatureType.Required)) ||
        storageKey.startsWith(this.getLocalStorageKeyPrefix(AbTestingFeatureType.Optional));

      if (isAbTestingLocalStorageKey) {
        callback(storageKey);
      }
    });
  }

  private cleanAllAbTestingLocalStorageEntries(): void {
    this.forEachAbTestingLocalStorageEntry((storageKey: string) => {
      this.localStorageService.delete(storageKey);
    });
  }

  /**
   * It's possible that the user went to the page in the past and due to a certain configuration some local storage entries got set.
   * Due to a config change some of those local storage entries are not relevant anymore. They will then get deleted.
   */
  private cleanNonConfiguredAbTestingLocalStorageEntries(): void {
    const addedFeatures = this.getAllEnabledFeatures().reduce((acc: string[], current: AbTestingFeature) => {
      acc.push(current.storyNumber);
      return acc;
    }, []);

    this.forEachAbTestingLocalStorageEntry((storageKey: string) => {
      const storageKeyStoryNumber = storageKey.split(OWCC_TICKET_PREFIX)[1];
      const isActiveAbTestingStorageKey = addedFeatures.includes(storageKeyStoryNumber);

      if (!isActiveAbTestingStorageKey) {
        this.localStorageService.delete(storageKey);
      }
    });
  }

  private getLocalStorageKeyPrefix(relevantType: AbTestingFeatureType): string {
    return OWCC_LOCAL_STORAGE_KEY.AB_TESTING_PREFIX.replace(
      AB_TESTING_TYPE_PLACEHOLDER,
      FEATURE_TYPE_TO_LOCAL_STORAGE_KEY_PART_MAPPING[relevantType]
    );
  }

  private getLocalStorageKey(type: AbTestingFeatureType, storyNumber: string): string {
    return `${this.getLocalStorageKeyPrefix(type)}${storyNumber}`;
  }

  private getLocalStorageValue(type: AbTestingFeatureType, storyNumber: string): AbTestingUserGroup | undefined {
    return this.localStorageService.get(this.getLocalStorageKey(type, storyNumber));
  }

  private init(): void {
    const getAbTestingConfig = (): Observable<AbTestingConfig> =>
      this.store.pipe(fromCarl.selectConfigOnce<AbTestingConfig>('abTesting'));

    getAbTestingConfig().subscribe((config: AbTestingConfig) => {
      const isAbTestingActiveInMarket = config.active;

      if (!isAbTestingActiveInMarket) {
        this.cleanAllAbTestingLocalStorageEntries();
      }

      this.treatOptionalFeatures(config) // requires user consent via CMP
        .subscribe(() => {
          this.cleanNonConfiguredAbTestingLocalStorageEntries();
        });
    });
  }

  private toTypedAbTestingFeatures(features: AbTestingFeature[], type: AbTestingFeatureType): TypedAbTestingFeature[] {
    return features?.map(feature => ({ ...feature, type }) as TypedAbTestingFeature) || [];
  }
}
